import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Plus, Search, Eye, Edit, Phone, Mail, MapPin, 
  Loader2, MessageSquare, Calendar, Clock, CheckCircle, 
  XCircle, Filter, ChevronDown, History, User, Building2
} from 'lucide-react';
import api from '../../utils/api';

const LEAD_SOURCES = [
  "Call", "Email", "WhatsApp", "Direct Visit", "India Mart", "Trade India", "By Reference", "Google", "Trade Shows", "Others"
];

const LEAD_STATUSES = [
  { value: "New", color: "bg-blue-500 text-white" },
  { value: "On Progress", color: "bg-yellow-500 text-white" },
  { value: "Success", color: "bg-green-500 text-white" },
  { value: "Failed", color: "bg-red-500 text-white" },
  { value: "Postponed", color: "bg-gray-500 text-white" }
];

const MobileLeads = () => {
  const { user: authUser } = useAuth();
  // Fallback to localStorage if AuthContext user is not available (mobile app flow)
  const localUser = React.useMemo(() => {
    try {
      const storedUser = localStorage.getItem('suntech_mobile_user') || localStorage.getItem('user');
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  }, []);
  const user = authUser || localUser;
  
  const [leads, setLeads] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assignedToFilter, setAssignedToFilter] = useState('all');
  const [leadSourceFilter, setLeadSourceFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Permission checks based on role
  const isAdmin = user?.role === 'Admin';
  const isBDMSE = user?.role === 'BDM & SE';
  const isBackOffice = user?.role === 'Back Office Executive';
  const isBackOfficeAccounts = user?.role === 'Backoffice Accounts Executive';
  const isSalesMarketing = user?.role === 'Sales & Marketing';
  
  // Can see all leads: Admin, Back Office Executive, Backoffice Accounts Executive
  // Can see only assigned: BDM & SE, Sales & Marketing
  const canViewAllLeads = isAdmin || isBackOffice || isBackOfficeAccounts;
  const canOnlyViewAssigned = isBDMSE || isSalesMarketing;
  
  // Can manage (edit, followup) a lead
  const canManageLead = (lead) => {
    if (canViewAllLeads) return true;
    if (canOnlyViewAssigned) {
      return lead.assigned_employee_id === user?.id;
    }
    return false;
  };
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [followupDialogOpen, setFollowupDialogOpen] = useState(false);
  const [followupHistoryOpen, setFollowupHistoryOpen] = useState(false);
  const [closeLeadDialogOpen, setCloseLeadDialogOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadFollowups, setLeadFollowups] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [customerType, setCustomerType] = useState('Existing');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  
  const initialFormData = {
    lead_source: 'Call',
    customer_id: '',
    customer_type: 'Existing',
    inquiry_details: '',
    assigned_employee_id: '',
    due_date: '',
    lead_status: 'New',
    non_customer_name: '',
    non_customer_mobile: '',
    interested_products: []
  };
  
  const [formData, setFormData] = useState(initialFormData);
  
  const [followupData, setFollowupData] = useState({
    followup_date: new Date().toISOString().split('T')[0],
    notes: '',
    next_due_date: '',
    status_update: ''
  });
  
  const [closureData, setClosureData] = useState({
    closure_status: 'Success',
    closure_notes: ''
  });

  // Fetch data
  const fetchLeads = useCallback(async () => {
    try {
      const response = await api.get('/leads/');
      // Sort by due_date (follow-up date) - earliest first, then by created_at for those without due_date
      const sortedLeads = response.data.sort((a, b) => {
        const dueDateA = a.next_followup_date || a.due_date;
        const dueDateB = b.next_followup_date || b.due_date;
        
        // If both have due dates, sort by due date (earliest first)
        if (dueDateA && dueDateB) {
          return new Date(dueDateA) - new Date(dueDateB);
        }
        // Leads with due dates come first
        if (dueDateA && !dueDateB) return -1;
        if (!dueDateA && dueDateB) return 1;
        // Fall back to created_at descending
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setLeads(sortedLeads);
    } catch (error) {
      toast.error('Failed to fetch leads');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await api.get('/customers/');
      setCustomers(response.data.filter(c => c.status === 'Active'));
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await api.get('/leads/assignable-employees');
      setEmployees(response.data);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchProducts = useCallback(async () => {
    try {
      const response = await api.get('/products/');
      setProducts(response.data.filter(p => p.status === 'Active'));
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchLeadFollowups = async (leadId) => {
    try {
      const response = await api.get(`/leads/${leadId}/followups`);
      setLeadFollowups(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchLeads();
    fetchCustomers();
    fetchEmployees();
    fetchProducts();
  }, [fetchLeads, fetchCustomers, fetchEmployees, fetchProducts]);

  // Filter leads based on permissions and search
  const filteredLeads = leads.filter(lead => {
    // Permission filter
    if (canOnlyViewAssigned && lead.assigned_employee_id !== user?.id) {
      return false;
    }
    
    // Get display values
    const displayName = lead.company_name || lead.non_customer_name || lead.customer?.company_name || '';
    const displayMobile = lead.mobile || lead.non_customer_mobile || lead.customer?.mobile || '';
    
    // Search filter
    const matchesSearch = 
      displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      displayMobile.includes(searchTerm) ||
      lead.lead_no?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.inquiry_details?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || lead.lead_status === statusFilter;
    
    // Assigned To filter
    const matchesAssignedTo = assignedToFilter === 'all' || lead.assigned_employee_id === assignedToFilter;
    
    // Lead Source filter
    const matchesLeadSource = leadSourceFilter === 'all' || lead.lead_source === leadSourceFilter;
    
    return matchesSearch && matchesStatus && matchesAssignedTo && matchesLeadSource;
  });

  // Helper functions
  const getStatusColor = (status) => {
    const found = LEAD_STATUSES.find(s => s.value === status);
    return found ? found.color : 'bg-gray-500 text-white';
  };

  const isFollowupOverdue = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followupDate = new Date(dateStr);
    followupDate.setHours(0, 0, 0, 0);
    return followupDate < today;
  };

  const isFollowupToday = (dateStr) => {
    if (!dateStr) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followupDate = new Date(dateStr);
    followupDate.setHours(0, 0, 0, 0);
    return followupDate.getTime() === today.getTime();
  };

  // Filtered customers for dropdown
  const filteredCustomers = customers.filter(c =>
    c.company_name?.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    c.mobile?.includes(customerSearchTerm) ||
    c.contact_person?.toLowerCase().includes(customerSearchTerm.toLowerCase())
  );

  // Form handlers
  const resetForm = () => {
    setFormData(initialFormData);
    setCustomerType('Existing');
    setCustomerSearchTerm('');
    // Auto-assign to self for field roles
    if (canOnlyViewAssigned && user?.id) {
      setFormData(prev => ({ ...prev, assigned_employee_id: user.id }));
    }
  };

  const openCreateDialog = () => {
    resetForm();
    // Set default due date to today
    const today = new Date().toISOString().split('T')[0];
    setFormData(prev => ({ ...prev, due_date: today }));
    // Auto-assign for field roles
    if (canOnlyViewAssigned && user?.id) {
      setFormData(prev => ({ ...prev, assigned_employee_id: user.id }));
    }
    setCreateDialogOpen(true);
  };

  const openEditDialog = (lead) => {
    setSelectedLead(lead);
    setFormData({
      lead_source: lead.lead_source || 'Call',
      customer_id: lead.customer_id || '',
      customer_type: lead.customer_type || 'Existing',
      inquiry_details: lead.inquiry_details || '',
      assigned_employee_id: lead.assigned_employee_id || '',
      due_date: lead.due_date || '',
      lead_status: lead.lead_status || 'New',
      non_customer_name: lead.non_customer_name || '',
      non_customer_mobile: lead.non_customer_mobile || '',
      interested_products: lead.interested_products || []
    });
    setCustomerType(lead.customer_type || 'Existing');
    setEditDialogOpen(true);
  };

  const openFollowupDialog = (lead) => {
    setSelectedLead(lead);
    setFollowupData({
      followup_date: new Date().toISOString().split('T')[0],
      notes: '',
      next_due_date: '',
      status_update: lead.lead_status
    });
    setFollowupDialogOpen(true);
  };

  const openFollowupHistory = async (lead) => {
    setSelectedLead(lead);
    await fetchLeadFollowups(lead.id);
    setFollowupHistoryOpen(true);
  };

  const openCloseLeadDialog = (lead) => {
    setSelectedLead(lead);
    setClosureData({
      closure_status: 'Success',
      closure_notes: ''
    });
    setCloseLeadDialogOpen(true);
  };

  // Submit handlers
  const handleCreateLead = async () => {
    if (!formData.lead_source || !formData.due_date) {
      toast.error('Please fill all required fields');
      return;
    }
    
    if (customerType === 'Existing' && !formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    
    if (customerType === 'Non-Customer' && (!formData.non_customer_name || !formData.non_customer_mobile)) {
      toast.error('Please enter customer name and mobile');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/leads/', {
        ...formData,
        customer_type: customerType
      });
      toast.success('Lead created successfully');
      setCreateDialogOpen(false);
      resetForm();
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create lead');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateLead = async () => {
    if (!selectedLead) return;
    
    setSubmitting(true);
    try {
      await api.put(`/leads/${selectedLead.id}`, {
        ...formData,
        customer_type: customerType
      });
      toast.success('Lead updated successfully');
      setEditDialogOpen(false);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddFollowup = async () => {
    if (!selectedLead || !followupData.notes) {
      toast.error('Please enter followup notes');
      return;
    }

    setSubmitting(true);
    try {
      // Handle keep_current value for status_update
      const submitData = {
        lead_id: selectedLead.id,
        ...followupData,
        status_update: followupData.status_update === 'keep_current' ? '' : followupData.status_update
      };
      
      await api.post(`/leads/${selectedLead.id}/followups`, submitData);
      toast.success('Followup added successfully');
      setFollowupDialogOpen(false);
      setFollowupData({
        followup_date: new Date().toISOString().split('T')[0],
        notes: '',
        next_due_date: '',
        status_update: ''
      });
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add followup');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseLead = async () => {
    if (!selectedLead || !closureData.closure_status) {
      toast.error('Please select closure status');
      return;
    }

    setSubmitting(true);
    try {
      await api.put(`/leads/${selectedLead.id}`, {
        lead_status: closureData.closure_status,
        closure_notes: closureData.closure_notes
      });
      toast.success('Lead closed successfully');
      setCloseLeadDialogOpen(false);
      fetchLeads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to close lead');
    } finally {
      setSubmitting(false);
    }
  };

  // Get display name for lead
  const getLeadDisplayName = (lead) => {
    if (lead.customer_type === 'Existing' && lead.customer) {
      return lead.customer.company_name;
    }
    return lead.non_customer_name || lead.company_name || 'Unknown';
  };

  const getLeadDisplayMobile = (lead) => {
    if (lead.customer_type === 'Existing' && lead.customer) {
      return lead.customer.mobile;
    }
    return lead.non_customer_mobile || lead.mobile || '';
  };

  // Count pending followups
  const pendingFollowupsCount = filteredLeads.filter(lead => 
    (isFollowupToday(lead.next_followup_date) || isFollowupOverdue(lead.next_followup_date)) &&
    lead.lead_status !== 'Success' && lead.lead_status !== 'Failed'
  ).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Leads & Followups</h1>
          <p className="text-sm text-gray-500">{filteredLeads.length} leads</p>
        </div>
        <Button onClick={openCreateDialog} size="sm" data-testid="add-lead-btn">
          <Plus className="w-4 h-4 mr-1" /> New
        </Button>
      </div>

      {/* Pending Followups Alert */}
      {pendingFollowupsCount > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-red-500" />
            <span className="text-sm font-medium text-red-700">
              {pendingFollowupsCount} Pending Followups
            </span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-red-600"
            onClick={() => setStatusFilter('all')}
          >
            View All
          </Button>
        </div>
      )}

      {/* Search & Filters */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="flex-1 min-w-[110px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {LEAD_STATUSES.map(status => (
                <SelectItem key={status.value} value={status.value}>{status.value}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Assigned To Filter */}
          <Select value={assignedToFilter} onValueChange={setAssignedToFilter}>
            <SelectTrigger className="flex-1 min-w-[110px]">
              <SelectValue placeholder="All Assigned" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Assigned</SelectItem>
              {employees.map(emp => (
                <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Lead Source Filter */}
          <Select value={leadSourceFilter} onValueChange={setLeadSourceFilter}>
            <SelectTrigger className="flex-1 min-w-[110px]">
              <SelectValue placeholder="All Sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {LEAD_SOURCES.map(source => (
                <SelectItem key={source} value={source}>{source}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Leads List */}
      {filteredLeads.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <Building2 className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No leads found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredLeads.map((lead) => {
            // Check if lead needs highlighting (follow-up today or overdue, not closed)
            const needsHighlight = (isFollowupToday(lead.next_followup_date) || 
              (isFollowupOverdue(lead.next_followup_date) && 
               lead.lead_status !== 'Success' && 
               lead.lead_status !== 'Failed'));
            
            return (
            <Card key={lead.id} className={`overflow-hidden ${needsHighlight ? 'bg-yellow-50 border-yellow-300' : ''}`} data-testid={`lead-card-${lead.id}`}>
              <CardContent className="p-4">
                {/* Header Row */}
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <span className="text-sm font-semibold text-blue-600">{lead.lead_no}</span>
                    <Badge className={`ml-2 ${getStatusColor(lead.lead_status)}`}>
                      {lead.lead_status}
                    </Badge>
                  </div>
                  <div className="text-xs text-gray-500">
                    {new Date(lead.created_at).toLocaleDateString('en-IN')}
                  </div>
                </div>

                {/* Customer Info */}
                <div className="space-y-2 mb-3">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">{getLeadDisplayName(lead)}</span>
                  </div>
                  {getLeadDisplayMobile(lead) && (
                    <a href={`tel:${getLeadDisplayMobile(lead)}`} className="flex items-center gap-2 text-blue-600">
                      <Phone className="w-4 h-4" />
                      <span>{getLeadDisplayMobile(lead)}</span>
                    </a>
                  )}
                  {lead.assigned_employee_name && (
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>Assigned to: {lead.assigned_employee_name}</span>
                    </div>
                  )}
                </div>

                {/* Inquiry Details */}
                {lead.inquiry_details && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{lead.inquiry_details}</p>
                )}

                {/* Next Followup */}
                {lead.next_followup_date && lead.lead_status !== 'Success' && lead.lead_status !== 'Failed' && (
                  <div className={`text-sm mb-3 p-2 rounded ${
                    isFollowupOverdue(lead.next_followup_date) 
                      ? 'bg-red-100 text-red-700' 
                      : isFollowupToday(lead.next_followup_date)
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-blue-50 text-blue-700'
                  }`}>
                    <Calendar className="w-4 h-4 inline mr-1" />
                    Next Followup: {new Date(lead.next_followup_date).toLocaleDateString('en-IN')}
                    {isFollowupToday(lead.next_followup_date) && ' (Today)'}
                    {isFollowupOverdue(lead.next_followup_date) && ' (Overdue)'}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => { setSelectedLead(lead); setViewDialogOpen(true); }}
                  >
                    <Eye className="w-4 h-4 mr-1" /> View
                  </Button>
                  
                  {canManageLead(lead) && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openEditDialog(lead)}
                      >
                        <Edit className="w-4 h-4 mr-1" /> Edit
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openFollowupDialog(lead)}
                        className="text-purple-600 border-purple-200"
                      >
                        <MessageSquare className="w-4 h-4 mr-1" /> Followup
                      </Button>
                      
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => openFollowupHistory(lead)}
                      >
                        <History className="w-4 h-4 mr-1" /> History
                      </Button>
                      
                      {lead.lead_status !== 'Success' && lead.lead_status !== 'Failed' && (
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => openCloseLeadDialog(lead)}
                          className="text-green-600 border-green-200"
                        >
                          <CheckCircle className="w-4 h-4 mr-1" /> Close
                        </Button>
                      )}
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
            );
          })}
        </div>
      )}

      {/* Create Lead Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Lead</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Customer Type */}
            <div>
              <Label>Customer Type *</Label>
              <Select value={customerType} onValueChange={(v) => {
                setCustomerType(v);
                setFormData(prev => ({ ...prev, customer_type: v, customer_id: '', non_customer_name: '', non_customer_mobile: '' }));
              }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Existing">Existing Customer</SelectItem>
                  <SelectItem value="Non-Customer">Non-Customer</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Existing Customer Selection */}
            {customerType === 'Existing' && (
              <div className="relative">
                <Label>Select Customer *</Label>
                <Input 
                  placeholder="Search customer by name or mobile..."
                  value={customerSearchTerm}
                  onChange={(e) => {
                    setCustomerSearchTerm(e.target.value);
                    setShowCustomerDropdown(true);
                  }}
                  onFocus={() => setShowCustomerDropdown(true)}
                />
                {showCustomerDropdown && customerSearchTerm && (
                  <div className="absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredCustomers.length > 0 ? (
                      filteredCustomers.slice(0, 10).map(c => (
                        <div 
                          key={c.id}
                          className="p-3 hover:bg-gray-100 cursor-pointer border-b"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, customer_id: c.id }));
                            setCustomerSearchTerm(c.company_name);
                            setShowCustomerDropdown(false);
                          }}
                        >
                          <p className="font-medium">{c.company_name}</p>
                          <p className="text-sm text-gray-600">
                            {c.district && <span className="text-blue-600">{c.district}</span>}
                            {c.district && c.mobile && ' • '}
                            {c.mobile}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="p-3 text-gray-500">No customers found</div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Non-Customer Fields */}
            {customerType === 'Non-Customer' && (
              <>
                <div>
                  <Label>Customer Name *</Label>
                  <Input 
                    value={formData.non_customer_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, non_customer_name: e.target.value }))}
                    placeholder="Enter customer name"
                  />
                </div>
                <div>
                  <Label>Mobile Number *</Label>
                  <Input 
                    type="tel"
                    value={formData.non_customer_mobile}
                    onChange={(e) => setFormData(prev => ({ ...prev, non_customer_mobile: e.target.value }))}
                    placeholder="Enter mobile number"
                  />
                </div>
              </>
            )}

            {/* Lead Source */}
            <div>
              <Label>Lead Source *</Label>
              <Select value={formData.lead_source} onValueChange={(v) => setFormData(prev => ({ ...prev, lead_source: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map(source => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Inquiry Details */}
            <div>
              <Label>Inquiry Details *</Label>
              <Textarea 
                value={formData.inquiry_details}
                onChange={(e) => setFormData(prev => ({ ...prev, inquiry_details: e.target.value }))}
                placeholder="Describe the inquiry..."
                rows={3}
              />
            </div>

            {/* Assigned Employee */}
            <div>
              <Label>Assigned To *</Label>
              <Select 
                value={formData.assigned_employee_id} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, assigned_employee_id: v }))}
                disabled={canOnlyViewAssigned}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select employee" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {canOnlyViewAssigned && (
                <p className="text-xs text-gray-500 mt-1">Auto-assigned to you</p>
              )}
            </div>

            {/* Due Date */}
            <div>
              <Label>Due Date *</Label>
              <Input 
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateLead} disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Lead Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Lead - {selectedLead?.lead_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Lead Status */}
            <div>
              <Label>Status</Label>
              <Select value={formData.lead_status} onValueChange={(v) => setFormData(prev => ({ ...prev, lead_status: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_STATUSES.map(status => (
                    <SelectItem key={status.value} value={status.value}>{status.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Lead Source */}
            <div>
              <Label>Lead Source</Label>
              <Select value={formData.lead_source} onValueChange={(v) => setFormData(prev => ({ ...prev, lead_source: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LEAD_SOURCES.map(source => (
                    <SelectItem key={source} value={source}>{source}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Inquiry Details */}
            <div>
              <Label>Inquiry Details</Label>
              <Textarea 
                value={formData.inquiry_details}
                onChange={(e) => setFormData(prev => ({ ...prev, inquiry_details: e.target.value }))}
                rows={3}
              />
            </div>

            {/* Assigned Employee */}
            {canViewAllLeads && (
              <div>
                <Label>Assigned To</Label>
                <Select 
                  value={formData.assigned_employee_id} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, assigned_employee_id: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Due Date */}
            <div>
              <Label>Due Date</Label>
              <Input 
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData(prev => ({ ...prev, due_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateLead} disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Lead Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedLead?.lead_no}</DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={getStatusColor(selectedLead.lead_status)}>{selectedLead.lead_status}</Badge>
                <span className="text-sm text-gray-500">via {selectedLead.lead_source}</span>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Customer</p>
                  <p className="font-medium">{getLeadDisplayName(selectedLead)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Mobile</p>
                  <p className="font-medium">{getLeadDisplayMobile(selectedLead) || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Assigned To</p>
                  <p className="font-medium">{selectedLead.assigned_employee_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Due Date</p>
                  <p className="font-medium">{selectedLead.due_date ? new Date(selectedLead.due_date).toLocaleDateString('en-IN') : 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created</p>
                  <p className="font-medium">{new Date(selectedLead.created_at).toLocaleString('en-IN')}</p>
                </div>
                <div>
                  <p className="text-gray-500">Followup Count</p>
                  <p className="font-medium">{selectedLead.followup_count || 0}</p>
                </div>
              </div>
              
              {selectedLead.inquiry_details && (
                <div>
                  <p className="text-gray-500 text-sm">Inquiry Details</p>
                  <p className="text-sm mt-1">{selectedLead.inquiry_details}</p>
                </div>
              )}

              {selectedLead.next_followup_date && (
                <div className={`p-3 rounded ${
                  isFollowupOverdue(selectedLead.next_followup_date) ? 'bg-red-100' : 'bg-blue-50'
                }`}>
                  <p className="text-sm font-medium">
                    Next Followup: {new Date(selectedLead.next_followup_date).toLocaleDateString('en-IN')}
                    {isFollowupOverdue(selectedLead.next_followup_date) && ' (Overdue)'}
                  </p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Followup Dialog */}
      <Dialog open={followupDialogOpen} onOpenChange={setFollowupDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Followup - {selectedLead?.lead_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Followup Date *</Label>
              <Input 
                type="date"
                value={followupData.followup_date}
                onChange={(e) => setFollowupData(prev => ({ ...prev, followup_date: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Notes *</Label>
              <Textarea 
                value={followupData.notes}
                onChange={(e) => setFollowupData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Enter followup notes..."
                rows={3}
              />
            </div>

            <div>
              <Label>Status Update</Label>
              <Select value={followupData.status_update} onValueChange={(v) => setFollowupData(prev => ({ ...prev, status_update: v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Keep current status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep_current">Keep current status</SelectItem>
                  {LEAD_STATUSES.map(status => (
                    <SelectItem key={status.value} value={status.value}>{status.value}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Next Followup Date</Label>
              <Input 
                type="date"
                value={followupData.next_due_date}
                onChange={(e) => setFollowupData(prev => ({ ...prev, next_due_date: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setFollowupDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleAddFollowup} disabled={submitting}>
              {submitting ? 'Adding...' : 'Add Followup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Followup History Dialog */}
      <Dialog open={followupHistoryOpen} onOpenChange={setFollowupHistoryOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Followup History - {selectedLead?.lead_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {leadFollowups.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No followups recorded</p>
            ) : (
              leadFollowups.map((followup, index) => (
                <Card key={followup.id || index}>
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start mb-2">
                      <span className="text-sm font-medium">
                        {new Date(followup.followup_date).toLocaleDateString('en-IN')}
                      </span>
                      {followup.status_update && (
                        <Badge variant="outline">{followup.status_update}</Badge>
                      )}
                    </div>
                    <p className="text-sm text-gray-600">{followup.notes}</p>
                    {followup.next_due_date && (
                      <p className="text-xs text-blue-600 mt-2">
                        Next: {new Date(followup.next_due_date).toLocaleDateString('en-IN')}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Close Lead Dialog */}
      <Dialog open={closeLeadDialogOpen} onOpenChange={setCloseLeadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Lead - {selectedLead?.lead_no}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Closure Status *</Label>
              <Select value={closureData.closure_status} onValueChange={(v) => setClosureData(prev => ({ ...prev, closure_status: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Success">Success (Won)</SelectItem>
                  <SelectItem value="Failed">Failed (Lost)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Closure Notes</Label>
              <Textarea 
                value={closureData.closure_notes}
                onChange={(e) => setClosureData(prev => ({ ...prev, closure_notes: e.target.value }))}
                placeholder="Add closure notes..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setCloseLeadDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCloseLead} disabled={submitting}>
              {submitting ? 'Closing...' : 'Close Lead'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileLeads;
